import React, { useState } from 'react';
import CircularProgress from '@/components/CircularProgress';
import Link from '@/components/Link';
import ProgressBar from '@/components/ProgressBar';
import { FiArrowRight } from '@/components/react-icons/main/index.esm';
import IconV2 from '@/components/v2/IconV2';
import YearlyToggle from '@/components/YearlyToggle';
import useUser from '@/hooks/useUser';
import formatNumber from '@/lib/formatNumber';
import { createCheckoutSession } from '@/redux/actions/settingsActions';
import selectors from '@/redux/selectors/selectors';
import * as Popover from '@radix-ui/react-popover';
import { productIds } from '@shared/schema/productIds.js';
import { FiArrowUpCircle } from '@/components/react-icons/main';
import { useSelector } from 'react-redux';
import Button from '../Button.jsx';
import getColor from '@/components/platform/platformCurrentPlan/getColor.js';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors.js';
import useBillingCycle from '@/hooks/useBillingCycle.js';

const PlatformCurrentPlan = React.memo(() => {
  const { percentage, formattedTasksUsed, tasksRemaining, limit } = useBillingCycle();
  const isLoading = useArgSelector(selectors.isLoading, 'getOrgState');
  const subscriptionName = useSelector(selectors.subscriptionName);
  const subscriptionId = useSelector((state) => selectors.subscription(state)?.id);
  const [isYearly, setIsYearly] = useState(true);
  const user = useUser();
  const email = user?.jwt?.email;

  const handleUpgradeClick = async () => {
    const { url } = await createCheckoutSession({ productId: productIds('production').professional, email, yearly: isYearly });
    window.open(url, '_self');
  };

  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <div className="platform-current-plan-trigger" style={{ pointerEvents: isLoading ? 'none' : 'auto' }}>
          <CircularProgress //
            size="md"
            getColor={getColor}
            hoverVariant
            hasPercentageChar
            percentage={percentage}
            isLoading={isLoading}
            className="platform-current-plan-circle"
            data-title={`${formattedTasksUsed} tasks used. You have ${tasksRemaining} tasks remaining.`}
          />
        </div>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content className="platform-current-plan-content" side="bottom" align="end" sideOffset={8}>
          <div className="plan-info">
            <div className="plan-info-labels">
              <span className="current-plan-label">Current Plan</span>
              <span className="current-plan-value">{subscriptionName}</span>
            </div>
            <Link to={'/user/billing'}>
              <Popover.Close asChild>
                <IconV2 icon={FiArrowRight} data-title="Go to your billing page" />
              </Popover.Close>
            </Link>
          </div>

          <div className="tasks-used">
            <span className="tasks-used-title">Tasks Used</span>
            <span className="tasks-used-desc">Tasks are counted over a 30 day rolling period.</span>

            <ProgressBar value={percentage} color="primary" />
            <span className="tasks-used-value">
              {formattedTasksUsed}/{formatNumber(limit)}
            </span>
          </div>

          <div className="upgrade-button">
            {!subscriptionId && (
              <div className="upgrade-button-basic-plan">
                <Button className="primary full-width" icon={FiArrowUpCircle} onClick={handleUpgradeClick}>
                  Upgrade To Professional Plan
                </Button>
                <YearlyToggle active={isYearly} onChange={setIsYearly} />
              </div>
            )}
            {subscriptionId && (
              <Popover.Close asChild>
                <Button size={'lg'} className="primary full-width" icon={FiArrowUpCircle} route={'/user/billing'}>
                  Upgrade your plan
                </Button>
              </Popover.Close>
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
});

PlatformCurrentPlan.displayName = 'PlatformCurrentPlan';

export default PlatformCurrentPlan;
